import { format } from 'date-fns';

import moment from 'moment-timezone';

// 22/02/2024
export const US_DATE_FORMAT = 'MM/dd/yyyy';
export const US_DATE_TIME_FORMAT = 'MM/DD/yyyy hh:mm a';

// 2024-Jul-20
export const ISO_DATE_FORMAT = 'yyyy-MM-dd';

/**
 * Converts an ISO 8601 date to MDY (MM/dd/yyyy) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in MM/dd/yyyy format.
 */
export const formatMDY = (dateString: string | Date) => {
  return format(new Date(dateString), US_DATE_FORMAT); // 05/07/2024
};

/**
 * Converts an ISO 8601 date to DMY (dd/MM/yyyy) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in dd/MM/yyyy format.
 */
export const formatDMY = (dateString: string | Date) => {
  return format(new Date(dateString), 'dd/MM/yyyy'); // 07/05/2024
};

/**
 * Converts an ISO 8601 date to YMD (yyyy-MM-dd) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in yyyy-MM-dd format.
 */
export const formatYMD = (dateString: string | Date) => {
  return format(new Date(dateString), ISO_DATE_FORMAT); // 2024-05-07
};

/**
 * Converts an ISO 8601 date to JUL (yy/DDD) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in yy/DDD format.
 */
export const formatJUL = (dateString: string | Date) => {
  return format(new Date(dateString), 'yy/DDD'); // 24/127
};

/**
 * Converts an ISO 8601 date to USA (MM/DD/YYYY) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in MM/DD/YYYY format.
 */
export const formatUSA = (dateString: string | Date) => {
  return format(new Date(dateString), US_DATE_FORMAT); // 05/07/2024
};
export const formatUSAWithDateTime = (dateString: string | Date) => {
  return format(new Date(dateString), 'MM/dd/yyyy hh:mm a'); // 05/07/2024
};

/**
 * Converts an ISO 8601 date to EUR (dd. MM. yyyy) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in dd. MM. yyyy format.
 */
export const formatEUR = (dateString: string | Date) => {
  return format(new Date(dateString), 'dd. MM. yyyy'); // 07. 05. 2024
};

/**
 * Converts an ISO 8601 date to JIS (yyyy-MM-dd) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in yyyy-MM-dd format.
 */
export const formatJIS = (dateString: string | Date) => {
  return format(new Date(dateString), 'yyyy-MM-dd'); // 2024-05-07
};

/**
 * Converts an ISO 8601 date to HH:mm:ss (24-hour clock) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted time string in HH:mm:ss format.
 */
export const format24Hour = (dateString: string | Date) => {
  return format(new Date(dateString), 'HH:mm:ss'); // 14:30:00
};

/**
 * Converts an ISO 8601 date to hh:mm:ss (12-hour clock) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted time string in hh:mm:ss a format.
 */
export const format12Hour = (dateString: string | Date) => {
  return format(new Date(dateString), 'hh:mm:ss a'); // 02:30:00 PM
};

/**
 * Converts an ISO 8601 date to hh:mm (12-hour clock without seconds) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted time string in hh:mm a format.
 */
export const format12HourWithoutSeconds = (dateString: string | Date) => {
  return format(new Date(dateString), 'hh:mm a'); // 02:30 PM
};

/**
 * Converts a time-only string from 24-hour format to 12-hour AM/PM format.
 * Accepts `HH:mm` or `HH:mm:ss` and preserves the original value when parsing fails.
 * @param {string | null | undefined} timeString - The 24-hour time string.
 * @returns {string} The formatted time string in `h:mm AM/PM` format.
 */
export const format24HourTimeStringToAmPm = (timeString?: string | null) => {
  if (!timeString) {
    return '';
  }

  const trimmedValue = timeString.trim();
  const [hoursStr, minutesStr] = trimmedValue.split(':');

  if (hoursStr === undefined || minutesStr === undefined) {
    return timeString;
  }

  let hours = Number(hoursStr);
  const minutes = minutesStr;

  if (!Number.isInteger(hours) || hours < 0 || hours > 23) {
    return timeString;
  }

  const meridiem = hours >= 12 ? 'PM' : 'AM';

  if (hours > 12) {
    hours -= 12;
  }

  if (hours === 0) {
    hours = 12;
  }

  return `${hours.toString().padStart(2, '0')}:${minutes} ${meridiem}`;
};

/**
 * Converts an ISO 8601 date to hh:mm:ss a (12-hour clock with AM/PM) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted time string in hh:mm:ss a format.
 */
export const format12HourAMPM = (dateString: string | Date) => {
  return format(new Date(dateString), 'hh:mm:ss a'); // 02:30:00 PM
};

/**
 * Converts an ISO 8601 date to HH:mm (24-hour clock without seconds) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted time string in HH:mm format.
 */
export const format24HourWithoutSeconds = (dateString: string | Date) => {
  return format(new Date(dateString), 'HH:mm'); // 14:30
};

/**
 * Converts an ISO 8601 date to HH:mm:ss.SSS (24-hour clock with milliseconds) format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted time string in HH:mm:ss.SSS format.
 */
export const format24HourMilliseconds = (dateString: string | Date) => {
  return format(new Date(dateString), 'HH:mm:ss.SSS'); // 14:30:00.000
};

/**
 * Converts an ISO 8601 date to MMM dd format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in MMM dd format.
 */
export const formatMMMdd = (dateString: string | Date) => {
  return format(new Date(dateString), 'MMM dd'); // "Mar 18", "Apr 09"
};

/**
 * Converts an ISO 8601 date to MMM dd, YYYY format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in MMM dd format.
 */
export const formatMMMddyyyy = (dateString: string | Date) => {
  const formattedDate = moment(dateString).format('MMM DD, yyyy'); // "Mar 18, 2024", "Apr 09, 2022"
  return formattedDate;
};

export const stripDate = (date: Date | null) => {
  if (!date) {
    return null;
  }
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

/**
 * Converts an ISO 8601 date to MMMM dd, YYYY format.
 * @param {string | Date} dateString - The ISO 8601 date string or Date object.
 * @returns {string} The formatted date string in MMMM dd format.
 */
export const formatMMMMddyyyy = (dateString: string | Date) => {
  return format(new Date(dateString), 'MMMM dd, yyyy'); // "Mar 18, 2024", "Apr 09, 2022"
};

export const formatEEE = (dateString: string | Date) => {
  return format(new Date(dateString), 'EEE MMM d'); // "Mon Sep 4"
};

// Wed, Sep 06, 2023
export const formatDayMonthYear = (dateString: string | Date) => {
  return format(new Date(dateString), 'EEE, MMM dd, yyyy'); // Wed, Sep 06, 2023
};

// Thu, Sep 17
export const formatDayMonth = (dateString: string | Date) => {
  const formattedData = moment(dateString).format('ddd, MMM DD'); // Thu, Sep 17
  return formattedData;
};
// format date to day
export const formatDay = (dateString: string | Date) => {
  return format(new Date(dateString), 'EEEE'); // "Monday"
};

export const formatmmddyyyy = (dateString: string | Date, existingFormat?: string) => {
  return moment(dateString, existingFormat).format('MM/DD/YYYY'); // "03/18/2024"
};
export function formatDateTimeForReport(date: string | Date, timeZone?: string): { date: string; time: string } {
  const m = timeZone
    ? moment.utc(date).tz(timeZone) // interpret as UTC, convert to timezone
    : moment(date);
  return {
    date: m.format('DD-MMM'), // e.g. 14-Jan
    time: m.format('h:mm A'), // e.g. 4:49:08 PM
  };
}

export function dayDifferenceBetweenDate(start: Date, end: Date): number {
  // Ensure the time difference is included
  const diffInMs = end.getTime() - start.getTime(); // Difference in milliseconds

  // Convert milliseconds to full days (taking time into account)
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

  // If the result is a fraction, we round up if the remaining time is more than half a day
  return Math.ceil(diffInDays);
}
export const formatDateTimeWithTz = (date: Date, timezone: string) => {
  return moment(date).tz(timezone).format('MM-DD-YYYY hh:mm A');
};
export const formatDateWithTz = (date: Date, timezone: string) => {
  return moment(date).tz(timezone).format('MM-DD-YYYY');
};
export const formatCurrentDateWithTz = (timezone: string) => {
  return moment.tz(timezone).format('MM-DD-YYYY');
};
export const formatLocalDateWithTz = (date: string | Date, timezone: string, outputFormat = 'MM-DD-YYYY') => {
  const parsedDateOnly =
    typeof date === 'string' ? moment(date, ['YYYY-MM-DD', 'MM-DD-YYYY', 'MM/DD/YYYY'], true) : moment.utc(date);

  const dateOnlyString = parsedDateOnly.isValid()
    ? parsedDateOnly.format('YYYY-MM-DD')
    : moment.utc(date).format('YYYY-MM-DD');

  return moment.tz(dateOnlyString, 'YYYY-MM-DD', timezone).format(outputFormat);
};
export const formatDateTime = (date: Date) => {
  return moment(date).format('MM-DD-YYYY hh:mm A');
};

/**
 * Flexible date formatter using Intl.DateTimeFormat under the hood.
 * Allows consumers to pick month/day/year styles and locale without
 * needing a new util for every pattern.
 */
export type FlexibleDateFormatOptions = {
  locale?: string;
  month?: 'numeric' | '2-digit' | 'long' | 'short' | 'narrow';
  day?: 'numeric' | '2-digit';
  year?: 'numeric' | '2-digit';
};

/**
 * Formats a single date using the provided options.
 * Defaults to en-US with short month, numeric day, numeric year (e.g., "Jan 1, 2024").
 */
export const formatDateWithOptions = (date: string | Date, options?: FlexibleDateFormatOptions) => {
  const { locale = 'en-US', month = 'short', day = 'numeric', year = 'numeric' } = options || {};
  const formatter = new Intl.DateTimeFormat(locale, { month, day, year });
  return formatter.format(new Date(date));
};

/**
 * Formats a date range using the provided options, joined by a separator.
 * Example: Jan 1, 2023 — Dec 31, 2024
 */
export const formatDateRangeWithOptions = (
  start: string | Date,
  end: string | Date,
  options?: FlexibleDateFormatOptions & { separator?: string }
) => {
  const separator = options?.separator ?? ' — ';
  const startText = formatDateWithOptions(start, options);
  const endText = formatDateWithOptions(end, options);
  return `${startText}${separator}${endText}`;
};

export const formatPickupTime = (dateStr?: string | null): string => {
  if (!dateStr) return 'N/A'; // handles null, undefined, or empty string

  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return 'N/A'; // invalid date check

  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

type TimeDiff = { value: number; unit: 'm' | 'h'; valueWithUnit?: string };

export const getTimeDiff = (dropOff?: string | null): TimeDiff => {
  if (!dropOff) return { value: 0, unit: 'm' };

  const now = moment.utc();
  const target = moment.utc(dropOff);

  if (!target.isValid()) return { value: 0, unit: 'm' };

  const diffInMinutes = target.diff(now, 'minutes');
  const positiveDiff = Math.max(diffInMinutes, 0);

  if (positiveDiff < 60) {
    return { value: positiveDiff, unit: 'm', valueWithUnit: `${positiveDiff} m` };
  } else {
    const diffInHours = Math.floor(positiveDiff / 60);
    return { value: diffInHours, unit: 'h', valueWithUnit: `${diffInHours} h` };
  }
};

export const phoneNumberInUSFormat = (phoneNumber: string): string => {
  if (!phoneNumber) return 'N/A';

  // Remove non-digits
  const digits = phoneNumber.replace(/\D/g, '');

  let normalized = digits;

  // Handle +1 or leading 1
  if (digits.length === 11 && digits.startsWith('1')) {
    normalized = digits.slice(1);
  }

  // Must be exactly 10 digits after normalization
  if (normalized.length !== 10) {
    return 'N/A';
  }

  const areaCode = normalized.slice(0, 3);
  const firstPart = normalized.slice(3, 6);
  const secondPart = normalized.slice(6);

  return `(${areaCode}) ${firstPart}-${secondPart}`;
};

export function formatMinutesToHHMMSS(minutes: number): string {
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const secs = 0;
  return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}
// Returns the given date with its year corrected: if the date (with current
// year) is before today, use next year; otherwise use current year. Used for
// timeoff cycle endDates stored with an incorrect year in the DB.
export const getSafeTimezone = (timezone?: string | null): string => {
  if (!timezone) return 'UTC';
  try {
    Intl.DateTimeFormat(undefined, { timeZone: timezone });
    return timezone;
  } catch {
    return 'UTC';
  }
};

export const getEndDateWithCorrectYear = (value: Date | string | null, timezone?: string | null): Date | null => {
  if (!value) return null;
  const source = timezone ? moment.tz(value, timezone) : moment(value);
  if (!source.isValid()) return null;
  const now = timezone ? moment.tz(timezone) : moment();
  const withCurrentYear = source.clone().year(now.year());
  const year = withCurrentYear.isBefore(now, 'day') ? now.year() + 1 : now.year();
  return source.year(year).toDate();
};
